import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { styled } from "@mui/material/styles";
import {
    Box,
    Grid,
    GridProps,
    IconButton,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { Videocam as VideocamIcon } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppHeader } from "JS/React/Components/AppHeader";
import { AppButton } from "JS/React/Components/AppButton";
import { AppCompletionProgress } from "JS/React/Components/AppCompletionProgress";
import { AppDownloadingIcons } from "JS/React/Components/Icon/AppDownloadingIcons";
import { AppTypography } from "JS/React/Components/AppTypography";
import { calculatePercentage, createCourseIDForStats } from "JS/Helpers";
import {
    useDeleteCourse,
    useDownloadedCourses,
    useGetLatestCourseStats,
    useWriteOfflineCourseStats,
} from "JS/React/Hooks/DownloadCourses";
import { useRouting } from "JS/React/Hooks/Routes";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useScrollToTop } from "JS/React/Hooks/Media";
import { useReceivedCourses } from "JS/React/Hooks/Course/Course";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";
import {
    CourseStep,
    DownloadedCourseDetail,
    EventActions,
    EventCategories,
    EventNames,
} from "JS/Models";
import { SentGiftType, toSentGift } from "JS/Models/Firebase/GiftDashboard";
import {
    AskConfirmationDialog,
    AskConfirmationDialogRenderProps,
} from "JS/React/Components/AskConfirmationDialog";
import { messaging } from "JS/Helpers/UserMessaging";
import { StepsListItem } from "../../Courses/StepsListItem";
import qs from "qs";

const Root = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const Grey500Text = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[500],
}));

const Grey500Box = styled(Box)(({ theme }) => ({
    color: theme.palette.grey[500],
}));

const GreyA100Title = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey.A100,
}));

const PlayerContainer = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    height: "60vh",
    [theme.breakpoints.down("lg")]: {
        height: "50vh",
    },
    [theme.breakpoints.down("sm")]: {
        height: "30vh",
    },
}));

const GraphicWrapper = styled("span")(({ theme }) => ({
    border: `5px solid ${theme.palette.common.white}`,
    borderRadius: "50%",
    height: "120px",
    width: "120px",
    marginRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

const GraphicIcon = styled(VideocamIcon)(({ theme }) => ({
    color: theme.palette.grey[400],
}));

const DummyImageBox = styled(Box)(({ theme }) => ({
    background: theme.palette.grey[900],
    height: "100%",
}));

const ReleaseText = styled(Typography)(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const IconsWrapper = styled(Box)(({ theme }) => ({
    margin: theme.spacing(1.5, 0),
}));

const Grey600Icon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const CompletionText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
    textAlign: "center",
    fontWeight: 500,
}));

const Grey600Description = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

export const OfflineCourseDetail = (props: OfflineCourseDetailProps) => {
    const { className, ...rest } = props;

    const [showDesc, setShowDesc] = useState(false);

    const {
        activeCourse,
        firebaseCourseStats,
        offlineCourseStats,
        progressVal,
        completedSteps,
    } = useActiveCourse();

    const {
        sortedSteps,
        goBack,
        handleStartResume,
        handleStepItemClick,
        handleDeleteIconClick,
    } = useHandlers(activeCourse, completedSteps);
    const { scrollTo } = useQueryParams();
    const { deleteCompleteCourse } = useDeleteCourse();
    useScrollToTop();
    const confirmationPopupBtnText = "Delete";
    const confirmationPopupMainText = messaging.confirmation.courseDelete;

    const handleDeleteClick = (courseNid: string) => {
        deleteCompleteCourse(courseNid).then(() => {
            goBack(+scrollTo);
        });
    };

    return (
        <>
            <AppHeader
                title={activeCourse?.title}
                canGoBack
                searchable={false}
                onBackClick={() => goBack(+scrollTo)}
            />
            <Root container className={className} {...rest}>
                {activeCourse && (
                    <>
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <GreyA100Title fontWeight={"bold"} variant="h6">
                                {activeCourse.title}
                            </GreyA100Title>
                        </Grid>
                        <Grid item xs={12} sx={{ color: "grey.500" }}>
                            <Grey500Text variant="body1">
                                {activeCourse.sku_id}
                            </Grey500Text>
                            <Box display="flex">
                                <ReleaseText variant="body1">
                                    Released:
                                </ReleaseText>
                                <Grey500Text variant="body1">
                                    {moment
                                        .unix(+activeCourse.releaseDate)
                                        .format("LL")}
                                </Grey500Text>
                            </Box>
                        </Grid>
                        <PlayerContainer item xs={12}>
                            <DummyImageBox
                                display={"flex"}
                                alignItems={"center"}
                                justifyContent={"flex-end"}
                            >
                                <GraphicWrapper>
                                    <GraphicIcon
                                        sx={{
                                            fontSize: "60px",
                                        }}
                                    />
                                </GraphicWrapper>
                            </DummyImageBox>
                        </PlayerContainer>
                        <Grid item xs={12}>
                            <AppCompletionProgress
                                loading={!firebaseCourseStats}
                                title={`Course Completion ${
                                    progressVal <= 100
                                        ? progressVal.toFixed(0)
                                        : 100
                                }%`}
                                value={progressVal <= 100 ? progressVal : 100}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <IconsWrapper
                                display="flex"
                                justifyContent="space-between"
                            >
                                <div>
                                    <AskConfirmationDialog
                                        popupMainText={
                                            confirmationPopupMainText
                                        }
                                        confirmText={confirmationPopupBtnText}
                                        cancelButtonText={"Cancel"}
                                        onConfirm={() =>
                                            handleDeleteClick(activeCourse?.nid)
                                        }
                                    >
                                        {(renderProps) => {
                                            return (
                                                <IconButton
                                                    onClick={() =>
                                                        handleDeleteIconClick(
                                                            renderProps,
                                                        )
                                                    }
                                                >
                                                    <AppDownloadingIcons iconType="trash" />
                                                </IconButton>
                                            );
                                        }}
                                    </AskConfirmationDialog>
                                </div>

                                <div>
                                    {progressVal >= 100 ? (
                                        <Box display="flex">
                                            <Grey600Icon
                                                style={{
                                                    fontSize: "18px",
                                                    marginRight: "8px",
                                                }}
                                                icon={["fas", "circle-check"]}
                                            />
                                            <CompletionText variant="body1">
                                                Completed
                                            </CompletionText>
                                        </Box>
                                    ) : (
                                        <>
                                            {!!activeCourse?.displayMode && (
                                                <AppButton
                                                    rounded
                                                    buttonVariant="grey-outlined"
                                                    onClick={handleStartResume}
                                                >
                                                    {progressVal <= 0
                                                        ? "Start"
                                                        : "Resume"}
                                                </AppButton>
                                            )}
                                        </>
                                    )}
                                </div>

                                <Box display={"flex"}>
                                    <IconButton
                                        onClick={() => setShowDesc(!showDesc)}
                                    >
                                        <Grey600Icon
                                            style={{
                                                fontSize: "22px",
                                            }}
                                            icon={["fas", "circle-info"]}
                                        />
                                    </IconButton>
                                    &nbsp;
                                </Box>
                            </IconsWrapper>
                        </Grid>
                        {!showDesc &&
                            firebaseCourseStats &&
                            sortedSteps.map((d, idx) => (
                                <Grid item xs={12} key={idx}>
                                    <StepsListItem
                                        isResume={
                                            offlineCourseStats?.steps &&
                                            offlineCourseStats?.steps[d.sku_id]
                                                ?.analytics?.last_played > 0
                                        }
                                        isDisabled={
                                            activeCourse.isSequenceOn &&
                                            idx > completedSteps
                                        }
                                        isQuiz={d.content_type === "quiz"}
                                        isCompleted={
                                            offlineCourseStats?.steps &&
                                            offlineCourseStats?.steps[d.sku_id]
                                                ?.analytics?.completed_count > 0
                                        }
                                        stepThumbnail={""}
                                        step={d}
                                        isLocked={false}
                                        isAllowClick
                                        onListItemClick={() =>
                                            handleStepItemClick(d, "false")
                                        }
                                        onPlayIconClick={() =>
                                            handleStepItemClick(d, "false")
                                        }
                                    />
                                </Grid>
                            ))}

                        {showDesc && (
                            <Grid item xs={12}>
                                <Grey600Description variant="body1">
                                    {activeCourse.description}
                                </Grey600Description>
                            </Grid>
                        )}
                    </>
                )}
            </Root>
        </>
    );
};

const useActiveCourse = () => {
    const params: any = useParams();
    const { id: contentId } = params;

    const { userDownloadedCourses, firebaseCourseStats } =
        useDownloadedCourses();

    const activeCourse = useMemo(() => {
        return userDownloadedCourses?.find((c) => c.nid === contentId);
    }, [userDownloadedCourses, contentId]);

    const memeberCourseSkuId = createCourseIDForStats(activeCourse?.sku_id);

    const offlineCourseStats =
        firebaseCourseStats && firebaseCourseStats[memeberCourseSkuId];

    const progressVal = calculatePercentage(
        offlineCourseStats?.analytics?.completed_steps,
        offlineCourseStats?.analytics?.total_steps,
    );

    const completedSteps = offlineCourseStats?.analytics
        ? offlineCourseStats.analytics.completed_steps
        : 0;

    return {
        activeCourse,
        offlineCourseStats,
        firebaseCourseStats,
        progressVal,
        completedSteps,
    };
};

const useHandlers = (
    activeCourse: DownloadedCourseDetail,
    completedSteps: number,
) => {
    const { writeOfflineFBCourseStats } = useWriteOfflineCourseStats();
    const { getLatestCourseStats } = useGetLatestCourseStats();
    const { sortedSteps, nextStep } = useVariables(
        activeCourse,
        completedSteps,
    );
    const { logFirebaseEvent } = useFirebaseLogger();

    const navigate = useNavigate();
    const { linkProvider } = useRouting();

    const { receivedCourses } = useReceivedCourses(true);

    const activeReceivedCourse = useMemo(() => {
        return receivedCourses?.find((c) => c.nid === activeCourse?.nid);
    }, [receivedCourses, activeCourse]);

    const { logGiftOpen } = useGiftDashboard(
        toSentGift(activeReceivedCourse),
        SentGiftType.COURSE,
        activeReceivedCourse?.sender?.member_id,
        false,
        null,
        true,
    );

    const onCourseOpen = async () => {
        const latestCourseStats = getLatestCourseStats(activeCourse?.nid);

        if (latestCourseStats) {
            writeOfflineFBCourseStats(activeCourse?.nid, {
                ...latestCourseStats,
                analytics: {
                    ...latestCourseStats?.analytics,
                    last_opened: moment().valueOf(),
                    opened_count: latestCourseStats.analytics.opened_count + 1,
                },
            });
        }

        if (activeCourse) {
            logFirebaseEvent(EventNames.COURSE_OPENED, {
                action: EventActions.OPEN,
                contentTitle: activeCourse.title,
                nId: activeCourse.nid,
                skuId: activeCourse.sku_id,
                category: EventCategories.COURSE,
            });
        }
    };
    const goBack = (scrollTo?: number) => {
        navigate(
            linkProvider.react
                .offline()
                .courses()
                .index({ scrollTo: scrollTo ?? 0 }),
        );
    };

    const handleStartResume = () => {
        if (nextStep) {
            navigate(
                linkProvider.react
                    .offline()
                    .courses()
                    .mediaStepDetail(
                        activeCourse.sku_id,
                        nextStep.sku_id,
                        "true",
                        activeCourse?.displayMode === 1,
                    ),
            );
        }
    };

    const handleStepItemClick = (d: CourseStep, isAutoPlay: string) =>
        navigate(
            linkProvider.react
                .offline()
                .courses()
                .mediaStepDetail(
                    activeCourse.sku_id,
                    d.sku_id,
                    isAutoPlay,
                    activeCourse?.displayMode === 1,
                ),
        );

    useEffect(() => {
        if (activeCourse) {
            onCourseOpen();
        }
    }, [activeCourse]);

    useEffect(() => {
        if (activeReceivedCourse) {
            logGiftOpen();
        }
    }, [activeReceivedCourse]);

    useEffect(() => {
        if (!activeCourse) {
            goBack();
        }
    }, [activeCourse]);

    const handleDeleteIconClick = (
        renderProps: AskConfirmationDialogRenderProps,
    ) => {
        renderProps.askConfirmation();
    };

    return {
        sortedSteps,
        handleStartResume,
        goBack,
        handleStepItemClick,
        handleDeleteIconClick,
    };
};

const useVariables = (
    activeCourse: DownloadedCourseDetail,
    completedSteps: number,
) => {
    const sortedSteps = activeCourse
        ? activeCourse?.steps
              .slice()
              .sort((a, b) => +a.position - +b.position)
              .filter((x) => x?.isDownloaded)
        : [];

    const nextStep = sortedSteps?.find((d) => +d.position === completedSteps);
    return {
        sortedSteps,
        nextStep,
    };
};
const useQueryParams = () => {
    const location = useLocation();

    return useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo: scrollTo,
        };
    }, [location?.search]);
};

export interface OfflineCourseDetailProps
    extends InternalStandardProps<GridProps> {}
